import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useUIStore, useMovieStore, useTimelineStore } from '../../../stores';
import { useState } from 'react';
import { useSignalR, convertSceneResponse } from '../../../hooks';
import { useTranslation } from '../../../i18n';
import { convertLayersResponseToTimelineData } from '../../../util/timeline';
import { parseStateProperties, resolveSceneObjectLayoutProps } from '../../../util/sceneObjectLayout';
import { resolveAssetUrl } from '../../../util/api';
import { resolveShapeFieldsFromBody } from '../../../util/shapeBody';
import { resolveTextFieldsFromBody } from '../../../util/textBody';
import { extractAssetPathFromBody, resolveImageFieldsFromBody } from '../../../util/imageBody';
import { Toolpad } from '../Toolpad';
import { ConfirmModal } from '../../ui/ConfirmModal';
export function SceneList() {
    const { t } = useTranslation();
    const { toolpads, toggleToolpad } = useUIStore();
    const { currentMovie, currentScene, currentMovieResponse, setCurrentSceneById, setCurrentScene, setCurrentLayer, setCurrentMovieResponse, updateScene, addScene, deleteScene, reorderScenes } = useMovieStore();
    const { loadTimelineData, resetTimeline } = useTimelineStore();
    const { status: signalRStatus, connect, sceneCreate, sceneDelete, sceneSave, timelineLayersOfScene } = useSignalR();
    const normalizeObjectSize = (value) => typeof value === 'number' && value > 0 ? Math.round(value) : 100;
    const mapLayeredSceneObjects = (layeredObjects) => {
        const ordered = [...layeredObjects]
            .sort((a, b) => a.sorter - b.sorter)
            .flatMap((layer) => (layer.sceneObjects ?? []).map((sceneObject) => ({ sceneObject, layerId: layer.id })));
        return ordered.map(({ sceneObject, layerId }, index) => {
            const response = sceneObject;
            const layer = layeredObjects.find((item) => item.id === layerId);
            const props = resolveSceneObjectLayoutProps(response, layer?.keyFrames);
            const rawSourcePath = response.url ?? response.fullPath ?? extractAssetPathFromBody(response.body);
            const fallbackState = {
                frame: 0,
                x: props.left ?? props.x ?? 0,
                y: props.top ?? props.y ?? 0,
                width: normalizeObjectSize(props.width ?? response.width),
                height: normalizeObjectSize(props.height ?? response.height),
                rotation: props.rotation ?? 0,
                opacity: props.opacity ?? 1,
            };
            const states = (layer?.keyFrames ?? [])
                .map((keyFrame) => {
                const stateForObject = keyFrame.objectStates?.find((objState) => objState.sceneObjectId === response.id);
                const parsed = parseStateProperties(stateForObject?.properties);
                if (!parsed)
                    return null;
                return {
                    frame: keyFrame.frameNumber,
                    x: Math.round(parsed.left ?? parsed.x ?? fallbackState.x),
                    y: Math.round(parsed.top ?? parsed.y ?? fallbackState.y),
                    width: normalizeObjectSize(parsed.width ?? fallbackState.width),
                    height: normalizeObjectSize(parsed.height ?? fallbackState.height),
                    rotation: parsed.rotation ?? fallbackState.rotation,
                    opacity: parsed.opacity ?? fallbackState.opacity,
                };
            })
                .filter((s) => s !== null)
                .sort((a, b) => a.frame - b.frame);
            const shapeFields = resolveShapeFieldsFromBody(response.type, response.body);
            const textFields = resolveTextFieldsFromBody(response.type, response.body, response.name);
            const imageFields = resolveImageFieldsFromBody(response.type, response.body);
            const { shapeType, pathData, fill, fillMode: shapeFillMode, fillGradient: shapeFillGradient, stroke, strokeWidth, strokeStyle, strokeSides, shadow: shapeShadow, glow: shapeGlow, } = shapeFields;
            const { fontSize, textAlign } = textFields;
            const textFill = textFields.fill;
            const resolvedFill = response.type === 'text' ? textFill : fill;
            const resolvedName = response.type === 'text' ? (textFields.name ?? response.name) : response.name;
            const resolvedFillMode = response.type === 'text' ? textFields.fillMode : response.type === 'shape' ? shapeFillMode : undefined;
            const resolvedFillGradient = response.type === 'text'
                ? textFields.fillGradient
                : response.type === 'shape'
                    ? shapeFillGradient
                    : undefined;
            const resolvedShadow = response.type === 'image'
                ? imageFields.shadow
                : response.type === 'text'
                    ? textFields.shadow
                    : shapeShadow;
            const resolvedGlow = response.type === 'image'
                ? imageFields.glow
                : response.type === 'text'
                    ? textFields.glow
                    : shapeGlow;
            return {
                id: String(response.id),
                type: response.type || 'image',
                name: resolvedName,
                src: rawSourcePath ? (resolveAssetUrl(rawSourcePath) || undefined) : undefined,
                libraryObjectId: response.libraryObjectId ?? undefined,
                parentObjectId: response.parentObjectId != null ? String(response.parentObjectId) : null,
                isGroup: response.isGroup === true || response.type === 'group',
                x: props.left ?? props.x ?? 0,
                y: props.top ?? props.y ?? 0,
                width: normalizeObjectSize(props.width ?? response.width),
                height: normalizeObjectSize(props.height ?? response.height),
                rotation: props.rotation ?? 0,
                opacity: props.opacity ?? 1,
                zIndex: response.sorter ?? index,
                visible: response.isVisible,
                locked: response.isLocked,
                pivotPoint: response.pivotPoint?.trim() ? response.pivotPoint : 'center',
                layerId,
                shapeType,
                pathData,
                fill: resolvedFill,
                fillMode: resolvedFillMode,
                fillGradient: resolvedFillGradient,
                stroke,
                strokeWidth,
                strokeStyle,
                strokeSides,
                shadow: resolvedShadow,
                glow: resolvedGlow,
                fontSize,
                textAlign,
                states: states.length > 0 ? states : [fallbackState],
            };
        });
    };
    const loadSceneData = async (sceneId, options) => {
        if (!currentMovieResponse)
            return;
        const movieState = useMovieStore.getState();
        const sceneResponse = options?.sceneResponse ??
            movieState.currentMovieResponse?.scenes.find((s) => String(s.id) === sceneId) ??
            currentMovieResponse.scenes.find((s) => String(s.id) === sceneId);
        const sceneInStore = options?.sceneInStore ??
            movieState.currentMovie?.scenes.find((s) => s.id === sceneId) ??
            currentMovie?.scenes.find((s) => s.id === sceneId);
        if (!sceneResponse || !sceneInStore)
            return;
        try {
            if (signalRStatus !== 'Connected') {
                await connect();
            }
            const layersResponse = await timelineLayersOfScene(sceneResponse);
            console.log('[SceneList] Timeline layers (with objects + keyframes + tweens + objectStates) loaded:', layersResponse);
            const mappedObjects = mapLayeredSceneObjects(layersResponse);
            const updatedScene = { ...sceneInStore, objects: mappedObjects };
            updateScene(sceneId, { objects: mappedObjects });
            setCurrentScene(updatedScene);
            const { layers, keyframes, tweens } = convertLayersResponseToTimelineData(layersResponse);
            loadTimelineData({ layers, keyframes, tweens });
            const topLayer = [...layers].sort((a, b) => a.order - b.order)[0] ?? null;
            setCurrentLayer(topLayer);
            console.log('[SceneList] Scene data loaded:', sceneId, { layers, keyframes, tweens });
        }
        catch (error) {
            console.error('[SceneList] Failed to load scene data:', sceneId, error);
        }
    };
    const isOpen = toolpads.sceneList.isOpen;
    const [editingSceneId, setEditingSceneId] = useState(null);
    const [draggedSceneId, setDraggedSceneId] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [isCreatingScene, setIsCreatingScene] = useState(false);
    const [pendingDeleteScene, setPendingDeleteScene] = useState(null);
    const [lastSceneWarningOpen, setLastSceneWarningOpen] = useState(false);
    const handleSceneSelect = async (sceneId) => {
        if (currentScene?.id === sceneId)
            return;
        resetTimeline();
        setCurrentSceneById(sceneId);
        await loadSceneData(sceneId);
    };
    const handleAddScene = async () => {
        if (!currentMovie || !currentMovieResponse || isCreatingScene)
            return;
        setIsCreatingScene(true);
        try {
            const newSceneResponse = {
                id: 0, // Will be set by backend
                movieId: currentMovieResponse.id,
                name: t('toolpads.scenes.defaultName', {
                    count: (currentMovie.scenes?.length ?? 0) + 1,
                }),
                description: '',
                framesTotal: 100,
                sorter: currentMovie.scenes?.length ?? 0,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };
            // Create scene on backend
            if (signalRStatus !== 'Connected') {
                await connect();
            }
            const createdScene = await sceneCreate(newSceneResponse);
            console.log('[SceneList] Scene created:', createdScene);
            // Convert to frontend Scene type and add to store
            const newScene = convertSceneResponse(createdScene);
            addScene(newScene);
            // Keep API snapshot in sync (use getState to avoid losing previously added scenes)
            const latestMovieResponse = useMovieStore.getState().currentMovieResponse;
            if (latestMovieResponse) {
                const scenes = latestMovieResponse.scenes.some((s) => s.id === createdScene.id)
                    ? latestMovieResponse.scenes
                    : [...latestMovieResponse.scenes, createdScene];
                setCurrentMovieResponse({ ...latestMovieResponse, scenes });
            }
            resetTimeline();
            setCurrentSceneById(newScene.id);
            // Backend creates a default layer; load it into the timeline UI
            await loadSceneData(newScene.id, {
                sceneResponse: createdScene,
                sceneInStore: newScene,
            });
        }
        catch (error) {
            console.error('[SceneList] Failed to create scene:', error);
            alert(t('toolpads.scenes.createFailed', {
                error: error instanceof Error ? error.message : t('common.unknownError'),
            }));
        }
        finally {
            setIsCreatingScene(false);
        }
    };
    const handleDeleteScene = async (sceneId) => {
        if (!currentMovie || !currentMovieResponse)
            return;
        if (currentMovie.scenes.length <= 1) {
            setLastSceneWarningOpen(true);
            return;
        }
        const sceneToDelete = currentMovie.scenes.find(s => s.id === sceneId);
        if (!sceneToDelete)
            return;
        setPendingDeleteScene({ id: sceneToDelete.id, name: sceneToDelete.name });
    };
    const confirmDeleteScene = async () => {
        if (!currentMovie || currentMovie.scenes.length <= 1 || !currentMovieResponse || !pendingDeleteScene)
            return;
        const sceneId = pendingDeleteScene.id;
        const deletedIndex = currentMovie.scenes.findIndex(s => s.id === sceneId);
        const remainingScenes = currentMovie.scenes.filter(s => s.id !== sceneId);
        const fallbackScene = deletedIndex > 0
            ? (remainingScenes[deletedIndex - 1] ?? remainingScenes[0])
            : remainingScenes[0];
        const nextSceneId = fallbackScene?.id ?? null;
        try {
            // Find the SceneResponse from currentMovieResponse
            const sceneResponse = currentMovieResponse.scenes.find(s => String(s.id) === sceneId);
            if (sceneResponse) {
                if (signalRStatus !== 'Connected') {
                    await connect();
                }
                await sceneDelete(sceneResponse);
                console.log('[SceneList] Scene deleted:', sceneId);
            }
            // Remove from local store + API snapshot
            deleteScene(sceneId);
            const latestMovieResponse = useMovieStore.getState().currentMovieResponse;
            if (latestMovieResponse) {
                setCurrentMovieResponse({
                    ...latestMovieResponse,
                    scenes: latestMovieResponse.scenes.filter((s) => String(s.id) !== sceneId),
                });
            }
            if (nextSceneId) {
                resetTimeline();
                setCurrentSceneById(nextSceneId);
                await loadSceneData(nextSceneId);
            }
        }
        catch (error) {
            console.error('[SceneList] Failed to delete scene:', error);
            alert(t('toolpads.scenes.deleteFailed', {
                error: error instanceof Error ? error.message : t('common.unknownError'),
            }));
        }
        finally {
            setPendingDeleteScene(null);
        }
    };
    const handleRenameScene = async (sceneId, newName) => {
        setEditingSceneId(null);
        if (!currentMovieResponse)
            return;
        // Update local store
        updateScene(sceneId, { name: newName });
        // Save to server
        const sceneResponse = currentMovieResponse.scenes.find(s => String(s.id) === sceneId);
        if (sceneResponse) {
            try {
                if (signalRStatus !== 'Connected') {
                    await connect();
                }
                await sceneSave({ ...sceneResponse, name: newName });
            }
            catch (error) {
                console.error('[SceneList] Failed to save scene name:', error);
            }
        }
    };
    const handleDragStart = (e, sceneId) => {
        setDraggedSceneId(sceneId);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', sceneId);
        // Add a slight delay to allow drag image to be set
        setTimeout(() => {
            if (e.dataTransfer) {
                e.dataTransfer.setDragImage(new Image(), 0, 0);
            }
        }, 0);
    };
    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };
    const handleDragLeave = () => {
        setDragOverIndex(null);
    };
    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        setDragOverIndex(null);
        if (!draggedSceneId || !currentMovie)
            return;
        const currentIndex = currentMovie.scenes.findIndex(s => s.id === draggedSceneId);
        if (currentIndex === -1 || currentIndex === dropIndex) {
            setDraggedSceneId(null);
            return;
        }
        // Create new ordered list
        const newScenes = [...currentMovie.scenes];
        const [removed] = newScenes.splice(currentIndex, 1);
        newScenes.splice(dropIndex, 0, removed);
        // Update order values and reorder
        const sceneIds = newScenes.map(s => s.id);
        reorderScenes(sceneIds);
        setDraggedSceneId(null);
    };
    const handleDragEnd = () => {
        setDraggedSceneId(null);
        setDragOverIndex(null);
    };
    return (_jsxs(Toolpad, { title: t('toolpads.scenes.title'), isOpen: isOpen, onToggle: () => toggleToolpad('sceneList'), children: [_jsxs("div", { className: "scene-list", children: [(currentMovie?.scenes ?? []).map((scene, index) => (_jsxs("div", { className: `scene-list-item flex ${currentScene?.id === scene.id ? 'selected' : ''} ${draggedSceneId === scene.id ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`, draggable: !editingSceneId, onDragStart: (e) => handleDragStart(e, scene.id), onDragOver: (e) => handleDragOver(e, index), onDragLeave: handleDragLeave, onDrop: (e) => handleDrop(e, index), onDragEnd: handleDragEnd, onClick: () => handleSceneSelect(scene.id), onDoubleClick: () => setEditingSceneId(scene.id), children: [_jsx("span", { className: "scene-drag-handle", title: t('toolpads.scenes.dragReorder'), children: "\u2630" }), _jsxs("span", { className: "scene-number", children: [index + 1, "."] }), editingSceneId === scene.id ? (_jsx("input", { type: "text", className: "scene-name-input w-24 flex-1", defaultValue: scene.name, autoFocus: true, "aria-label": t('toolpads.scenes.nameAria'), onBlur: (e) => handleRenameScene(scene.id, e.target.value), onKeyDown: (e) => {
                                    if (e.key === 'Enter')
                                        handleRenameScene(scene.id, e.currentTarget.value);
                                    if (e.key === 'Escape')
                                        setEditingSceneId(null);
                                }, onClick: (e) => e.stopPropagation() })) : (_jsx("span", { className: "scene-name", children: scene.name })), _jsx("button", { className: "scene-delete", onClick: (e) => {
                                    e.stopPropagation();
                                    handleDeleteScene(scene.id);
                                }, title: t('toolpads.scenes.deleteTooltip'), children: "\u00D7" })] }, scene.id))), (!currentMovie?.scenes || currentMovie.scenes.length === 0) && (_jsx("div", { className: "scene-list-empty", children: t('toolpads.scenes.empty') })), _jsx("div", { className: "scene-list-actions", children: _jsx("button", { className: "btn", onClick: handleAddScene, disabled: isCreatingScene || !currentMovieResponse, children: isCreatingScene ? t('common.creating') : t('toolpads.scenes.add') }) })] }), _jsx(ConfirmModal, { open: !!pendingDeleteScene, onOpenChange: (open) => {
                    if (!open) {
                        setPendingDeleteScene(null);
                    }
                }, title: t('toolpads.scenes.deleteTitle'), description: pendingDeleteScene
                    ? t('toolpads.scenes.deleteDescription', { name: pendingDeleteScene.name })
                    : undefined, confirmText: t('common.delete'), cancelText: t('common.cancel'), onConfirm: () => void confirmDeleteScene() }), _jsx(ConfirmModal, { open: lastSceneWarningOpen, onOpenChange: setLastSceneWarningOpen, title: t('toolpads.scenes.cannotDeleteTitle'), description: t('toolpads.scenes.cannotDeleteLast'), confirmText: t('common.ok'), cancelText: t('common.close'), onConfirm: () => setLastSceneWarningOpen(false) })] }));
}
